import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Tool } from '../types';
import { mockTools } from '../data/mockData';

interface ToolsContextType {
  tools: Tool[];
  userTools: Tool[];
  borrowedTools: Tool[];
  loading: boolean;
  fetchTools: () => Promise<void>;
  addTool: (tool: Omit<Tool, 'id'>) => Promise<Tool>;
  updateTool: (id: string, updates: Partial<Tool>) => Promise<Tool>;
  deleteTool: (id: string) => Promise<void>;
}

const ToolsContext = createContext<ToolsContextType | undefined>(undefined);

export const useTools = () => {
  const context = useContext(ToolsContext);
  if (context === undefined) {
    throw new Error('useTools must be used within a ToolsProvider');
  }
  return context;
};

interface ToolsProviderProps {
  children: ReactNode;
}

export const ToolsProvider: React.FC<ToolsProviderProps> = ({ children }) => {
  const [tools, setTools] = useState<Tool[]>(mockTools);
  const [userTools, setUserTools] = useState<Tool[]>(mockTools.slice(0, 3)); // Mock user's tools
  const [borrowedTools, setBorrowedTools] = useState<Tool[]>(mockTools.slice(3, 5)); // Mock borrowed tools
  const [loading, setLoading] = useState(false);
  
  const fetchTools = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      // In a real app, we would fetch tools from an API
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch tools', error);
      setLoading(false);
    }
  };
  
  const addTool = async (tool: Omit<Tool, 'id'>): Promise<Tool> => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const newTool: Tool = {
        id: `tool-${Date.now()}`,
        ...tool,
      };
      
      setTools(prevTools => [...prevTools, newTool]);
      setUserTools(prevTools => [...prevTools, newTool]);
      
      setLoading(false);
      return newTool;
    } catch (error) {
      console.error('Failed to add tool', error);
      setLoading(false);
      throw new Error('Failed to add tool');
    }
  };
  
  const updateTool = async (id: string, updates: Partial<Tool>): Promise<Tool> => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const updatedTool = { ...tools.find(t => t.id === id)!, ...updates };
      
      setTools(prevTools => 
        prevTools.map(tool => tool.id === id ? updatedTool : tool)
      );
      
      setUserTools(prevTools => 
        prevTools.map(tool => tool.id === id ? updatedTool : tool)
      );
      
      setLoading(false);
      return updatedTool;
    } catch (error) {
      console.error('Failed to update tool', error);
      setLoading(false);
      throw new Error('Failed to update tool');
    }
  };
  
  const deleteTool = async (id: string): Promise<void> => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setTools(prevTools => prevTools.filter(tool => tool.id !== id));
      setUserTools(prevTools => prevTools.filter(tool => tool.id !== id));
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to delete tool', error);
      setLoading(false);
      throw new Error('Failed to delete tool');
    }
  };
  
  const value = {
    tools,
    userTools,
    borrowedTools,
    loading,
    fetchTools,
    addTool,
    updateTool,
    deleteTool,
  };
  
  return <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>;
};